import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type QuerySkeletonLayout =
  | "customerDetail"
  | "visitReport"
  | "visitDossier"
  | "supportThread";

const SK_3 = ["sk-3-a", "sk-3-b", "sk-3-c"] as const;
const SK_4 = ["sk-4-a", "sk-4-b", "sk-4-c", "sk-4-d"] as const;
const SK_5 = ["sk-5-a", "sk-5-b", "sk-5-c", "sk-5-d", "sk-5-e"] as const;
const SK_6 = [
  "sk-6-a",
  "sk-6-b",
  "sk-6-c",
  "sk-6-d",
  "sk-6-e",
  "sk-6-f",
] as const;
const SK_8 = [
  "sk-8-a",
  "sk-8-b",
  "sk-8-c",
  "sk-8-d",
  "sk-8-e",
  "sk-8-f",
  "sk-8-g",
  "sk-8-h",
] as const;

function Busy({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" className={className}>
      <span role="status" className="sr-only">
        {label ?? t("Loading…")}
      </span>
      {children}
    </div>
  );
}

function PageHeaderSkeleton({
  subtitle = true,
  action,
  children,
}: {
  subtitle?: boolean;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-[13px] pt-4 pb-1.5 md:px-5 md:pt-[22px] md:pb-[11px]">
      <div className="flex min-w-0 flex-col gap-[5px]">
        <Skeleton className="h-[22px] w-40 md:h-[26px] md:w-52" />
        {subtitle ? (
          <Skeleton className="my-0.5 hidden h-4 w-full max-w-2xl md:block" />
        ) : null}
        {children}
      </div>
      {action}
    </header>
  );
}

function TableRowsSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
      <div className="hidden h-10 items-center gap-4 border-border border-b px-4 md:flex">
        <Skeleton className="h-3 w-[28%]" />
        <Skeleton className="h-3 w-[18%]" />
        <Skeleton className="ml-auto h-3 w-16" />
      </div>
      {SK_8.slice(0, rows).map((key) => (
        <div
          key={key}
          className="flex h-[54px] items-center gap-3 border-border border-b px-3 last:border-b-0 md:px-4"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-[min(100%,220px)]" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="hidden h-3 w-20 md:block" />
          <Skeleton className="size-8 shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/** `/customers` while account list loads. */
export function CustomersPageSkeleton() {
  return (
    <Busy className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageHeaderSkeleton />
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-[13px] pt-2 pb-3 md:pt-0 md:pr-[15px] md:pb-5 md:pl-5">
          <div className="flex shrink-0 items-center gap-2 md:gap-2.5">
            <Skeleton className="h-10 min-w-0 flex-1 rounded-md md:w-[440px] md:flex-none" />
            <Skeleton className="hidden h-10 w-[108px] rounded-md md:block" />
            <Skeleton className="size-12 rounded-md md:hidden" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col pt-[22px] md:pt-4">
            <TableRowsSkeleton />
          </div>
        </main>
      </div>
    </Busy>
  );
}

/** `/connections` while connection rows load. */
export function ConnectionsPageSkeleton() {
  return (
    <Busy className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <PageHeaderSkeleton />
      <div className="flex gap-2 border-border border-b px-6 py-3">
        <Skeleton className="h-8 w-28 rounded-md" />
        <Skeleton className="h-8 w-36 rounded-md" />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto">
        {SK_5.map((key) => (
          <li
            key={key}
            className="flex flex-col gap-3 border-border border-b px-6 py-4 last:border-b-0 sm:flex-row sm:items-end sm:justify-between"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Skeleton className="mt-0.5 size-[18px] shrink-0 rounded-sm" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-[min(100%,240px)]" />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[280px]">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-9 w-full rounded-md sm:w-28" />
            </div>
          </li>
        ))}
      </ul>
    </Busy>
  );
}

/** Code-split route chunks and other unknown authed pages. */
export function RoutePendingSkeleton() {
  return <CustomersPageSkeleton />;
}

function CustomerDetailPageSkeleton() {
  return (
    <Busy className="flex min-h-0 flex-1 flex-col">
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 lg:overflow-hidden lg:pr-[18px] lg:pl-5">
        <div className="shrink-0 pt-[21px] pb-[33px] lg:pt-6 lg:pb-[39px]">
          <Skeleton className="h-9 w-40 rounded-md" />
        </div>
        <div className="flex flex-col gap-[23px] lg:min-h-0 lg:flex-1 lg:flex-row lg:gap-5">
          <div className="shrink-0 lg:w-[445px] lg:overflow-y-auto">
            <div className="flex flex-col gap-4 rounded-xl bg-card p-5 ring-1 ring-foreground/10">
              <Skeleton className="h-8 w-[min(100%,280px)]" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-36" />
              <div className="grid grid-cols-2 gap-3 pt-2">
                {SK_6.map((key) => (
                  <div key={key} className="flex flex-col gap-1.5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-full max-w-[140px]" />
                  </div>
                ))}
              </div>
              <Skeleton className="mt-2 h-36 w-full rounded-lg" />
              <div className="flex gap-2 pt-1">
                <Skeleton className="h-11 flex-1 rounded-md" />
                <Skeleton className="size-11 rounded-md" />
                <Skeleton className="size-11 rounded-md" />
              </div>
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-[27px] lg:min-h-0 lg:flex-1 lg:gap-4">
            <div className="flex gap-2 overflow-hidden lg:gap-0.5 lg:border-border lg:border-b">
              {SK_6.map((key) => (
                <Skeleton
                  key={key}
                  className="h-11 w-24 shrink-0 rounded-full lg:h-[41px] lg:w-[88px] lg:rounded-none"
                />
              ))}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              {SK_4.map((key) => (
                <Skeleton key={key} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </main>
    </Busy>
  );
}

function VisitReportPageSkeleton() {
  return (
    <Busy className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="shrink-0 px-3 pt-[21px] lg:px-5 lg:pt-5">
          <Skeleton className="h-9 w-52 rounded-md" />
        </div>
        <PageHeaderSkeleton
          subtitle={false}
          action={<Skeleton className="h-7 w-36 rounded-md" />}
        />
        <div className="flex gap-[22px] px-3 pt-[14px] pb-6 lg:px-5">
          <aside className="sticky top-0 hidden w-[214px] shrink-0 flex-col gap-2 self-start lg:flex">
            {SK_6.map((key) => (
              <Skeleton key={key} className="h-9 w-full rounded-md" />
            ))}
          </aside>
          <div className="flex min-w-0 max-w-[944px] flex-1 flex-col gap-[15px]">
            <Skeleton className="h-28 w-full rounded-xl" />
            {SK_3.map((key) => (
              <div
                key={key}
                className="flex flex-col gap-3 rounded-xl bg-card p-5 ring-1 ring-foreground/10"
              >
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-24 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 border-border border-t bg-card px-3 py-3 lg:px-5">
        <Skeleton className="h-10 w-28 rounded-md" />
        <Skeleton className="ml-auto h-10 w-36 rounded-md" />
      </div>
    </Busy>
  );
}

function VisitDossierPageSkeleton() {
  return (
    <Busy className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 p-6">
          <Skeleton className="h-11 w-48 max-w-full rounded-md" />
          <Skeleton className="h-9 w-72 max-w-full" />
          <Skeleton className="h-28 w-full rounded-lg" />
          {SK_5.map((key) => (
            <div
              key={key}
              className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 shadow-sm"
            >
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="border-border border-t bg-card px-6 py-4">
        <Skeleton className="ml-auto h-10 w-40 rounded-md" />
      </div>
    </Busy>
  );
}

const SUPPORT_INBOX_CARDS = SK_6;
const SUPPORT_THREAD_BUBBLES = SK_6;

/** `/support` while the escalated threads load. */
export function SupportInboxSkeleton() {
  const { t } = useTranslation();
  return (
    <Busy
      label={t("Loading support threads…")}
      className="flex min-h-0 flex-1 flex-col"
    >
      <PageHeaderSkeleton />
      <div className="min-h-0 flex-1 overflow-y-auto px-[13px] pb-6 md:px-5">
        <div className="flex flex-col gap-2">
          {SUPPORT_INBOX_CARDS.map((key) => (
            <div
              key={key}
              className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3 py-2.5"
            >
              <div className="flex h-5 items-center gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="my-0.5 h-4 w-[min(100%,220px)]" />
            </div>
          ))}
        </div>
      </div>
    </Busy>
  );
}

function SupportThreadSkeleton() {
  const { t } = useTranslation();
  return (
    <Busy
      label={t("Loading the conversation…")}
      className="flex min-h-0 flex-1"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between px-[13px] pt-2 md:px-5 md:pt-4">
          <Skeleton className="-ml-1.5 h-7 w-36 rounded-md" />
          <Skeleton className="size-7 rounded-md md:hidden" />
        </div>
        <PageHeaderSkeleton
          subtitle={false}
          action={<Skeleton className="hidden h-8 w-48 rounded-md md:block" />}
        >
          <Skeleton className="my-0.5 h-4 w-[min(100%,320px)]" />
        </PageHeaderSkeleton>
        <div className="flex min-h-0 flex-1 flex-col-reverse overflow-hidden px-[13px] pr-10 pb-4 md:px-5">
          <div className="flex flex-col gap-3">
            {SUPPORT_THREAD_BUBBLES.map((key, index) => {
              const fromEndUser = index % 2 === 0;
              const lineTone = fromEndUser ? undefined : "bg-foreground/10";
              return (
                <div
                  key={key}
                  className={cn(
                    "flex flex-col gap-1 rounded-lg border px-3 py-2",
                    fromEndUser
                      ? "w-[min(85%,420px)] self-start border-border bg-card"
                      : "w-[min(85%,360px)] self-end border-transparent bg-muted"
                  )}
                >
                  <Skeleton className={cn("my-[3px] h-3.5 w-32", lineTone)} />
                  <Skeleton className={cn("my-0.5 h-4 w-full", lineTone)} />
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 border-border border-t px-[13px] py-3 md:px-5">
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      </div>
      <div className="hidden w-10 shrink-0 border-border border-l md:block" />
      <Skeleton className="fixed top-1/3 right-0 h-[164px] w-8 rounded-r-none md:hidden" />
    </Busy>
  );
}

export function querySkeletonLayout(layout: QuerySkeletonLayout): ReactNode {
  switch (layout) {
    case "visitReport":
      return <VisitReportPageSkeleton />;
    case "visitDossier":
      return <VisitDossierPageSkeleton />;
    case "supportThread":
      return <SupportThreadSkeleton />;
    default:
      return <CustomerDetailPageSkeleton />;
  }
}
